/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';
import Retracked from 'js/lib/retracked';
import { isAuthenticatedUser } from 'js/lib/user';

import PageFooter from 'bundles/page-footer/components/PageFooter';
import PageHeader from 'bundles/page-header/components/PageHeader';
import withFetchLazyImages from 'bundles/page/components/shared/utils/withFetchLazyImages';
import { NotFoundError, RedirectError } from 'bundles/ssr/lib/errors/directives';
import GetCourseStatusQuery from 'bundles/video-logged-out-page/queries/GetCourseStatusQuery.graphql';
import type {
  GetCourseStatusQuery as GetCourseStatusQueryData,
  GetCourseStatusQueryVariables,
} from 'bundles/video-logged-out-page/queries/__generated__/GetCourseStatusQuery';

import 'css!bundles/video-logged-out-page/styl/index';

const COURSE_SLUG_DOES_NOT_EXIST = 'not found';

type PropsFromStores = {
  csrfToken: string;
};

type PropsFromRouter = PropsFromStores & {
  courseSlug: string;
};

type PropsFromGetCourseStatusQuery = {
  isLaunchedCourse: boolean;
  isLaunchedCourseDataLoading?: boolean;
};

type Props = PropsFromRouter & PropsFromGetCourseStatusQuery;

type State = {
  componentDidMount: boolean;
};

export class VideoLoggedOutApp extends React.LegacyComponentWithChildren<Props, State> {
  static contextTypes = {
    getStore: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof VideoLoggedOutApp.contextTypes>;

  state = {
    componentDidMount: false,
  };

  componentDidMount() {
    this.setState(() => ({
      componentDidMount: true,
    }));
  }

  render() {
    const { children, isLaunchedCourse, isLaunchedCourseDataLoading, courseSlug } = this.props;
    const { componentDidMount } = this.state;
    const { getStore } = this.context;
    const isAuthenticated = isAuthenticatedUser();

    if (!componentDidMount) {
      const errors = getStore && getStore('NaptimeStore').errors;
      if (
        errors &&
        Object.values(errors).some((message) => {
          if (message && typeof message === 'string') {
            const lowerCaseMessage = message.toLowerCase();
            return lowerCaseMessage.includes(COURSE_SLUG_DOES_NOT_EXIST);
          }
          return false;
        })
      ) {
        // if the slug is invalid, we're throwing an error and node render will propagate the error to edge
        // to render a static 404 page
        throw new NotFoundError();
      } else if (!isLaunchedCourseDataLoading && !isLaunchedCourse) {
        throw new RedirectError(301, `/learn/${courseSlug}`);
      }
    }

    return (
      <div className="rc-VideoLoggedOutApp" data-catchpoint="vlp-app">
        <PageHeader isPageWithSecondaryCta={true} hideMetaNav={isAuthenticated} showLanguagesDropdown />
        {children}
        <PageFooter />
      </div>
    );
  }
}

export default compose<Props, {}>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'video_logged_out_page',
      page: 'video_lecture_landing_page',
    },
  })),
  connectToStores<{ csrfToken: string }>(['ApplicationStore'], ({ ApplicationStore }) => ({
    csrfToken: ApplicationStore.csrfToken,
  })),
  connectToRouter<{ courseSlug: string }>(({ params: { courseSlug } }) => ({
    courseSlug,
  })),
  graphql<PropsFromRouter, GetCourseStatusQueryData, GetCourseStatusQueryVariables, PropsFromGetCourseStatusQuery>(
    GetCourseStatusQuery,
    {
      options: ({ courseSlug }) => ({
        variables: {
          slug: courseSlug,
        },
        context: { clientName: 'gatewayGql' },
        skip: !courseSlug,
      }),
      props: ({ data }) => {
        let isLaunchedCourse = false;
        const courseStatusData = data?.Course?.queryBySlug;
        const now = Date.now();

        if (courseStatusData && courseStatusData.launchedAt) {
          isLaunchedCourse = new Date(courseStatusData.launchedAt).valueOf() < now && courseStatusData.isReal;
        }

        return { isLaunchedCourse, isLaunchedCourseDataLoading: data?.loading };
      },
    }
  ),
  withFetchLazyImages
)(VideoLoggedOutApp);
